import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'zh-TW' | 'zh-CN';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (text: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// 繁簡體對照表（已去除重複鍵）
const translations: Record<string, Record<Language, string>> = {
  // 通用
  '開始測試': { 'zh-TW': '開始測試', 'zh-CN': '开始测试' },
  '下一題': { 'zh-TW': '下一題', 'zh-CN': '下一题' },
  '上一題': { 'zh-TW': '上一題', 'zh-CN': '上一题' },
  '完成測試': { 'zh-TW': '完成測試', 'zh-CN': '完成测试' },
  '提交': { 'zh-TW': '提交', 'zh-CN': '提交' },
  '確認': { 'zh-TW': '確認', 'zh-CN': '确认' },
  '取消': { 'zh-TW': '取消', 'zh-CN': '取消' },
  '返回': { 'zh-TW': '返回', 'zh-CN': '返回' },
  '繼續': { 'zh-TW': '繼續', 'zh-CN': '继续' },

  // 測試頁面
  '配對測試': { 'zh-TW': '配對測試', 'zh-CN': '配对测试' },
  '了解自己，遇見真愛': { 'zh-TW': '了解自己，遇見真愛', 'zh-CN': '了解自己，遇见真爱' },
  '通過55道精心設計的問題，深入了解你的性格特質、價值觀和愛情觀。': {
    'zh-TW': '通過55道精心設計的問題，深入了解你的性格特質、價值觀和愛情觀。',
    'zh-CN': '通过55道精心设计的问题，深入了解你的性格特质、价值观和爱情观。'
  },
  '這將幫助我們為你推薦最合適的伴侶。': {
    'zh-TW': '這將幫助我們為你推薦最合適的伴侶。',
    'zh-CN': '这将帮助我们为你推荐最合适的伴侣。'
  },
  '測試說明': { 'zh-TW': '測試說明', 'zh-CN': '测试说明' },
  '約需15分鐘': { 'zh-TW': '約需15分鐘', 'zh-CN': '约需15分钟' },
  '請在安靜的環境下完成，確保答案的準確性': {
    'zh-TW': '請在安靜的環境下完成，確保答案的準確性',
    'zh-CN': '请在安静的环境下完成，确保答案的准确性'
  },
  '誠實作答': { 'zh-TW': '誠實作答', 'zh-CN': '诚实作答' },
  '請根據真實感受回答，沒有標準答案': {
    'zh-TW': '請根據真實感受回答，沒有標準答案',
    'zh-CN': '请根据真实感受回答，没有标准答案'
  },
  '隱私保護': { 'zh-TW': '隱私保護', 'zh-CN': '隐私保护' },
  '你的答案將被嚴格保密，僅用於配對分析': {
    'zh-TW': '你的答案將被嚴格保密，僅用於配對分析',
    'zh-CN': '你的答案将被严格保密，仅用于配对分析'
  },

  // 性格維度
  '性格維度分析': { 'zh-TW': '性格維度分析', 'zh-CN': '性格维度分析' },
  '外向性': { 'zh-TW': '外向性', 'zh-CN': '外向性' },
  '開放性': { 'zh-TW': '開放性', 'zh-CN': '开放性' },
  '情緒穩定性': { 'zh-TW': '情緒穩定性', 'zh-CN': '情绪稳定性' },
  '責任感': { 'zh-TW': '責任感', 'zh-CN': '责任感' },
  '親和性': { 'zh-TW': '親和性', 'zh-CN': '亲和性' },
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('zh-TW');

  // 從本地儲存讀取使用者偏好
  useEffect(() => {
    const saved = localStorage.getItem('language') as Language | null;
    if (saved && (saved === 'zh-TW' || saved === 'zh-CN')) {
      setLanguageState(saved);
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  // 翻譯函式
  const t = (text: string): string => {
    // 先嘗試在字典中找對應翻譯
    if (translations[text] && translations[text][language]) {
      return translations[text][language];
    }

    // 若字典中未找到，使用簡易繁簡體轉換（僅在簡體模式下使用）
    if (language === 'zh-CN') {
      return convertToSimplified(text);
    }

    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// 簡單的繁簡轉換函數（僅對常見字進行映射）
function convertToSimplified(text: string): string {
  const map: Record<string, string> = {
    '測': '测', '試': '试', '發': '发', '現': '现', '適': '适', '夥': '伙',
    '伴': '伴', '過': '过', '學': '学', '瞭': '晓', '與': '与', '題': '题',
    '資': '资', '料': '料', '別': '别', '齡': '龄', '區': '区', '話': '话',
    '請': '请', '輸': '输', '電': '电', '驗': '验', '證': '证', '碼': '码',
    '傳': '传', '訊': '讯', '報': '报', '告': '告', '優': '优', '點': '点',
    '質': '质', '議': '议', '戀': '恋', '愛': '爱', '對': '对', '維': '维',
    '開': '开', '緒': '绪', '穩': '稳', '親': '亲', '邀': '邀', '鎖': '锁',
    '連': '连', '結': '结', '複': '复', '製': '制', '專': '专', '屬': '属',
    '進': '进', '確': '确', '認': '认', '繼': '继', '續': '续', '還': '还',
    '個': '个', '們': '们', '這': '这', '樣': '样', '應': '应', '該': '该',
    '會': '会', '為': '为', '從': '从', '來': '来', '時': '时', '間': '间',
    '問': '问', '關': '关', '於': '于', '將': '将', '幫': '帮', '助': '助',
    '薦': '荐', '靜': '静', '環': '环', '境': '境', '準': '准', '誠': '诚',
    '實': '实', '據': '据', '標': '标', '嚴': '严', '格': '格', '僅': '仅',
    '析': '析', '責': '责', '任': '任', '見': '见', '遇': '遇', '設': '设',
    '計': '计', '深': '深', '觀': '观', '推': '推', '保': '保', '密': '密',
    '感': '感', '受': '受', '答': '答', '案': '案', '沒': '没'
  };
  return text.split('').map(char => map[char] || char).join('');
}